import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { getSocket, initializeSocket } from "../utils/socket";
import ChatRestriction from "./ChatRestriction";
import SendGift from "./SendGift"; // Thêm import SendGift
import { useNavigate, Link } from "react-router-dom";
import { FaCrown, FaTrash, FaHeart, FaCopy, FaReply, FaShare, FaThumbtack, FaStar, FaComments, FaExternalLinkAlt, FaTimes, FaUndo, FaGift } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const ChatWidgetParticipants = ({ onSelectChat, onClose, users = [], friendsList = [] }) => {
  const { user: authUser, token, setToken, fetchWithRetry } = useAuth();
  const navigate = useNavigate();
  const [chatSessions, setChatSessions] = useState([]);
  const [activeChatUserId, setActiveChatUserId] = useState(null);
  const [messages, setMessages] = useState({});
  const [messageInput, setMessageInput] = useState("");
  const [notification, setNotification] = useState(null);
  const [isNotiFading, setIsNotiFading] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const socketRef = useRef(null);
  const chatEndRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState({});
  const [markedMessages, setMarkedMessages] = useState({});
  const [replyToMessage, setReplyToMessage] = useState(null);
  const pendingMessageIdRef = useRef(null);
  const [selectedMessages, setSelectedMessages] = useState([]); // Thêm state cho tin nhắn được chọn
  const [expandedMessages, setExpandedMessages] = useState({}); // Thêm state cho tin nhắn mở rộng
  const [recallTimeLimit, setRecallTimeLimit] = useState(60); // Giới hạn thời gian thu hồi
  const [showGiftModal, setShowGiftModal] = useState(false); // Thêm state cho modal quà tặng

  const showNotification = useCallback((message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => {
      setIsNotiFading(true);
      setTimeout(() => {
        setNotification(null);
        setIsNotiFading(false);
      }, 300);
    }, 2000);
  }, []);

  const getUserInfo = useCallback(
    (userId) => {
      if (!userId || typeof userId !== "string" || userId.includes("friendsList")) {
        console.warn(`ID người dùng không hợp lệ: ${userId}`);
        showNotification(`ID người dùng không hợp lệ: ${userId}`, "error");
        return {
          _id: userId,
          name: "Không xác định",
          role: "user",
          primaryPhoto: { photoUrl: "" },
          avatar: "",
          isOnline: false,
          membershipId: userId,
        };
      }
      const user = [...users, ...friendsList].find((u) => String(u._id) === String(userId));
      if (!user) {
        console.warn(`Không tìm thấy người _

System: dùng với ID ${userId} trong danh sách users hoặc friendsList.`);
        return {
          _id: userId,
          name: "Không xác định",
          role: "user",
          primaryPhoto: { photoUrl: "" },
          avatar: "",
          isOnline: false,
          membershipId: userId,
        };
      }
      return {
        ...user,
        role: user?.role || "user",
        primaryPhoto: user?.primaryPhoto || { photoUrl: "" },
        avatar: user?.avatar || "",
        isOnline: user?.isOnline ?? false,
        membershipId: user?.membershipId || user._id,
      };
    },
    [users, friendsList, showNotification]
  );

  const getVipIconColor = useCallback((role) => {
    switch (role) {
      case "vip_gold":
        return "text-yellow-500";
      case "vip_platinum":
        return "text-gray-300";
      case "vip_diamond":
        return "text-blue-500";
      default:
        return "text-yellow-500";
    }
  }, []);

  const getAvatarUrl = useCallback((user) => {
    return user?.primaryPhoto?.photoUrl || user?.avatar || "https://placehold.co/50";
  }, []);

  const getLastReceivedMessageId = useCallback(
    (targetUserId) => {
      const currentMessages = messages[targetUserId] || [];
      for (let i = currentMessages.length - 1; i >= 0; i--) {
        if (
          currentMessages[i].senderId !== authUser._id &&
          !(currentMessages[i].isSystemMessage || currentMessages[i].isHidden)
        ) {
          return currentMessages[i]._id;
        }
      }
      return null;
    },
    [messages, authUser]
  );

  const handleSessionContextMenu = useCallback((e, userId) => {
    e.preventDefault();
    setContextMenu({
      x: e.pageX,
      y: e.pageY,
      userId,
      type: "session",
    });
  }, []);

  const handleMessageContextMenu = useCallback((e, message) => {
    e.preventDefault();
    setContextMenu({
      x: e.pageX,
      y: e.pageY,
      messageId: message._id,
      messageContent: message.content,
      type: "message",
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleOpenChatWindow = useCallback(
    (userId) => {
      setActiveChatUserId(userId);
      closeContextMenu();
    },
    [closeContextMenu]
  );

  const handleOpenChatPage = useCallback(
    (userId) => {
      navigate(`/chat/${userId}`);
      closeContextMenu();
    },
    [navigate, closeContextMenu]
  );

  const handleCloseChatSession = useCallback(
    (userId) => {
      setChatSessions((prev) => prev.filter((id) => id !== userId));
      if (activeChatUserId === userId) setActiveChatUserId(null);
      closeContextMenu();
    },
    [activeChatUserId, closeContextMenu]
  );

  const handleLikeMessage = useCallback(
    (messageId) => {
      const message = messages[activeChatUserId]?.find((msg) => msg._id === messageId);
      if (!message) return;

      const newLikedState = !message.liked;

      setMessages((prev) => ({
        ...prev,
        [activeChatUserId]: prev[activeChatUserId].map((msg) =>
          msg._id === messageId ? { ...msg, liked: newLikedState } : msg
        ),
      }));

      const socket = getSocket();
      if (socket) {
        socket.emit("likeMessage", { messageId, liked: newLikedState }, (response) => {
          if (response?.error) {
            console.error("Lỗi từ server khi like tin nhắn:", response.error);
            showNotification(response.error.message || "Không thể like tin nhắn!", "error");
            setMessages((prev) => ({
              ...prev,
              [activeChatUserId]: prev[activeChatUserId].map((msg) =>
                msg._id === messageId ? { ...msg, liked: !newLikedState } : msg
              ),
            }));
          }
        });
      }

      closeContextMenu();
    },
    [messages, activeChatUserId, showNotification, closeContextMenu]
  );

  const handleRecallMessage = useCallback(
    async (messageId) => {
      const message = messages[activeChatUserId]?.find((msg) => msg._id === messageId);
      if (!message) return;

      const sentTime = new Date(message.createdAt);
      const currentTime = new Date();
      const timeDiff = (currentTime - sentTime) / (1000 * 60);

      if (timeDiff > recallTimeLimit) {
        showNotification(`Tin nhắn không thể thu hồi, đã gửi hơn ${recallTimeLimit} phút.`, "error");
        closeContextMenu();
        return;
      }

      const previousMessages = [...(messages[activeChatUserId] || [])];
      try {
        const socket = getSocket();
        if (socket) {
          socket.emit("recallMessage", { messageId }, (response) => {
            if (response?.error) {
              console.error("Lỗi từ server khi thu hồi tin nhắn:", response.error);
              showNotification(response.error.message || "Không thể thu hồi tin nhắn!", "error");
              setMessages((prev) => ({
                ...prev,
                [activeChatUserId]: previousMessages,
              }));
            } else {
              showNotification("Thu hồi tin nhắn thành công!", "success");
            }
          });
        } else {
          const res = await fetchWithRetry({
            method: "post",
            url: "/api/chat/recall-message",
            data: { messageId },
          });
          showNotification(res.data.message || "Thu hồi tin nhắn thành công!", "success");
        }
        closeContextMenu();
      } catch (error) {
        console.error("Lỗi khi thu hồi tin nhắn:", error);
        setMessages((prev) => ({
          ...prev,
          [activeChatUserId]: previousMessages,
        }));
        showNotification(
          error.response?.data?.message || "Lỗi khi thu hồi tin nhắn, vui lòng thử lại!",
          "error"
        );
      }
    },
    [messages, activeChatUserId, fetchWithRetry, showNotification, closeContextMenu, recallTimeLimit]
  );

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      const previousMessages = [...(messages[activeChatUserId] || [])];
      try {
        setMessages((prev) => ({
          ...prev,
          [activeChatUserId]: prev[activeChatUserId].filter((msg) => msg._id !== messageId),
        }));
        setPinnedMessages((prev) => ({
          ...prev,
          [activeChatUserId]: prev[activeChatUserId]?.filter((id) => id !== messageId) || [],
        }));
        setMarkedMessages((prev) => ({
          ...prev,
          [activeChatUserId]: prev[activeChatUserId]?.filter((id) => id !== messageId) || [],
        }));
        setSelectedMessages((prev) => prev.filter((id) => id !== messageId));

        await fetchWithRetry({
          method: "post",
          url: "/api/chat/delete-messages",
          data: { messageIds: [messageId] },
        });
        showNotification("Xóa tin nhắn thành công!", "success");
        closeContextMenu();
      } catch (error) {
        console.error("Lỗi khi xóa tin nhắn:", error);
        setMessages((prev) => ({
          ...prev,
          [activeChatUserId]: previousMessages,
        }));
        showNotification(
          error.response?.data?.message || "Lỗi khi xóa tin nhắn, vui lòng thử lại!",
          "error"
        );
      }
    },
    [messages, activeChatUserId, fetchWithRetry, showNotification, closeContextMenu]
  );

  const handleDeleteSelectedMessages = useCallback(
    async () => {
      const previousMessages = [...(messages[activeChatUserId] || [])];
      try {
        setMessages((prev) => ({
          ...prev,
          [activeChatUserId]: prev[activeChatUserId].filter((msg) => !selectedMessages.includes(msg._id)),
        }));
        setPinnedMessages((prev) => ({
          ...prev,
          [activeChatUserId]: prev[activeChatUserId]?.filter((id) => !selectedMessages.includes(id)) || [],
        }));
        setMarkedMessages((prev) => ({
          ...prev,
          [activeChatUserId]: prev[activeChatUserId]?.filter((id) => !selectedMessages.includes(id)) || [],
        }));
        setSelectedMessages([]);

        await fetchWithRetry({
          method: "post",
          url: "/api/chat/delete-messages",
          data: { messageIds: selectedMessages },
        });
        showNotification("Xóa các tin nhắn đã chọn thành công!", "success");
        closeContextMenu();
      } catch (error) {
        console.error("Lỗi khi xóa các tin nhắn đã chọn:", error);
        setMessages((prev) => ({
          ...prev,
          [activeChatUserId]: previousMessages,
        }));
        showNotification(
          error.response?.data?.message || "Lỗi khi xóa các tin nhắn, vui lòng thử lại!",
          "error"
        );
      }
    },
    [messages, activeChatUserId, selectedMessages, fetchWithRetry, showNotification, closeContextMenu]
  );

  const handleDeleteAllMessages = useCallback(
    async (userId) => {
      if (!window.confirm("Bạn có chắc chắn muốn xóa tất cả tin nhắn trong cuộc trò chuyện này không?")) {
        return;
      }
      const previousMessages = [...(messages[userId] || [])];
      try {
        setMessages((prev) => ({
          ...prev,
          [userId]: [],
        }));
        setPinnedMessages((prev) => ({
          ...prev,
          [userId]: [],
        }));
        setMarkedMessages((prev) => ({
          ...prev,
          [userId]: [],
        }));
        setSelectedMessages([]);

        await fetchWithRetry({
          method: "delete",
          url: `/api/chat/delete-all/${userId}`,
        });
        showNotification("Đã xóa tất cả tin nhắn!", "success");
        closeContextMenu();
      } catch (error) {
        console.error("Lỗi khi xóa tất cả tin nhắn:", error);
        setMessages((prev) => ({
          ...prev,
          [userId]: previousMessages,
        }));
        showNotification(
          error.response?.data?.message || "Lỗi khi xóa tất彼此: Lỗi khi xóa tất cả tin nhắn, vui lòng thử lại!",
          "error"
        );
      }
    },
    [fetchWithRetry, showNotification, messages, closeContextMenu]
  );

  const handleCopyMessage = useCallback(
    (content) => {
      navigator.clipboard.writeText(content).then(() => {
        showNotification("Đã sao chép tin nhắn!", "success");
      });
      closeContextMenu();
    },
    [showNotification, closeContextMenu]
  );

  const handleReplyMessage = useCallback(
    (messageId) => {
      setReplyToMessage(messages[activeChatUserId]?.find((msg) => msg._id === messageId));
      closeContextMenu();
    },
    [messages, activeChatUserId, closeContextMenu]
  );

  const handleShareMessage = useCallback(
    (messageId) => {
      showNotification("Chia sẻ tin nhắn: Chức năng đang phát triển!", "info");
      closeContextMenu();
    },
    [showNotification, closeContextMenu]
  );

  const handleShareSelectedMessages = useCallback(() => {
    const selectedContent = messages[activeChatUserId]
      ?.filter((msg) => selectedMessages.includes(msg._id))
      .map((msg) => msg.content)
      .join("\n");
    navigator.clipboard.writeText(selectedContent).then(() => {
      showNotification("Đã sao chép các tin nhắn để chia sẻ!", "success");
      setSelectedMessages([]);
      closeContextMenu();
    });
  }, [messages, activeChatUserId, selectedMessages, showNotification, closeContextMenu]);

  const handlePinMessage = useCallback(
    (messageId) => {
      const action = pinnedMessages[activeChatUserId]?.includes(messageId) ? "unpin" : "pin";
      setPinnedMessages((prev) => {
        const currentPins = prev[activeChatUserId] || [];
        return {
          ...prev,
          [activeChatUserId]: action === "pin" ? [...currentPins, messageId] : currentPins.filter((id) => id !== messageId),
        };
      });
      const socket = getSocket();
      if (socket) {
        socket.emit("pinMessage", { messageId, action }, (response) => {
          if (response?.error) {
            console.error(`Lỗi từ server khi ${action} tin nhắn:`, response.error);
            setPinnedMessages((prev) => {
              const currentPins = prev[activeChatUserId] || [];
              return {
                ...prev,
                [activeChatUserId]: action === "pin" ? currentPins.filter((id) => id !== messageId) : [...currentPins, messageId],
              };
            });
            showNotification(response.error.message || `Không thể ${action} tin nhắn!`, "error");
          } else {
            setPinnedMessages((prev) => ({
              ...prev,
              [activeChatUserId]: response.data.map((pin) => pin.messageId),
            }));
            showNotification(`Đã ${action === "pin" ? "ghim" : "bỏ ghim"} tin nhắn thành công!`, "success");
          }
        });
      }
      closeContextMenu();
    },
    [pinnedMessages, activeChatUserId, showNotification, closeContextMenu]
  );

  const handleMarkMessage = useCallback(
    (messageId) => {
      setMarkedMessages((prev) => {
        const currentMarks = prev[activeChatUserId] || [];
        return {
          ...prev,
          [activeChatUserId]: currentMarks.includes(messageId)
            ? currentMarks.filter((id) => id !== messageId)
            : [...currentMarks, messageId],
        };
      });
      closeContextMenu();
    },
    [activeChatUserId, closeContextMenu]
  );

  const handleSendGift = useCallback(
    async (gift, coinBalance) => {
      // Kiểm tra dữ liệu đầu vào tối thiểu
      if (!activeChatUserId) {
        showNotification("Không thể gửi quà: Người nhận không hợp lệ!", "error");
        throw new Error("Người nhận không hợp lệ!");
      }
      if (!gift || !gift._id) {
        showNotification("Không thể gửi quà: Quà tặng không hợp lệ!", "error");
        throw new Error("Quà tặng không hợp lệ!");
      }
  
      try {
        const response = await fetchWithRetry({
          method: "post",
          url: "/api/users/send-gift",
          data: { receiverId: activeChatUserId, giftId: gift._id },
        });
  
        // Kiểm tra phản hồi từ server
        if (response.status === 200) {
          showNotification(`Đã gửi quà "${gift.name}" thành công!`, "success");
  
          // Tạo tin nhắn quà tặng với createdAt là đối tượng Date
          const giftMessage = {
            _id: `gift-${Date.now()}`,
            senderId: authUser._id,
            receiverId: activeChatUserId,
            content: "[GIFT]",
            createdAt: new Date(), // Giữ dưới dạng đối tượng Date
            isSystemMessage: false,
            isGift: true,
            gift: {
              id: gift._id,
              name: gift.name,
              imageUrl: gift.imageUrl,
              coinPrice: gift.coinPrice,
            },
          };
  
          // Thêm tin nhắn quà tặng vào danh sách
          setMessages((prev) => ({
            ...prev,
            [activeChatUserId]: [...(prev[activeChatUserId] || []), giftMessage],
          }));
  
          // Đóng modal (đã được xử lý trong SendGift.js, nhưng giữ để đảm bảo)
          setShowGiftModal(false);
        } else {
          throw new Error("Phản hồi từ server không hợp lệ!");
        }
      } catch (error) {
        console.error("Lỗi khi gửi quà tặng:", error);
  
        // Xử lý lỗi chi tiết
        let errorMessage = "Không thể gửi quà tặng, vui lòng thử lại!";
        if (error.response) {
          if (error.response.status === 400) {
            errorMessage = error.response.data.message || "Dữ liệu gửi đi không hợp lệ!";
          } else if (error.response.status === 500) {
            errorMessage = "Lỗi server, vui lòng thử lại sau!";
          } else {
            errorMessage = error.response.data.message || errorMessage;
          }
        }
  
        showNotification(errorMessage, "error");
        throw error; // Ném lỗi để SendGift.js có thể xử lý
      }
    },
    [fetchWithRetry, activeChatUserId, authUser, showNotification]
  );
  
 

  const refreshChatHistory = useCallback(
    async (targetUserId) => {
      if (!targetUserId) return;
      try {
        const res = await fetchWithRetry({
          method: "get",
          url: `/api/chat/history/${targetUserId}`,
        });
        const fetchedMessages = res.data.messages || [];
        setMessages((prev) => ({
          ...prev,
          [targetUserId]: fetchedMessages.map((msg) => ({
            ...msg,
            createdAt: new Date(msg.createdAt),
            readTimestamp: msg.readTimestamp ? new Date(msg.readTimestamp) : null,
            isTemp: false,
            liked: msg.liked || false,
            isSystemMessage: msg.isSystemMessage || false,
            isGift: msg.isGift || false,
            gift: msg.gift || null,
            originalMessageId: msg.originalMessageId || null,
          })),
        }));
        const conversationId = [authUser._id, targetUserId].sort().join("_");
        const cachedPinned = localStorage.getItem(`pinnedMessages_${conversationId}`);
        if (cachedPinned) {
          setPinnedMessages((prev) => ({
            ...prev,
            [targetUserId]: JSON.parse(cachedPinned),
          }));
        }
        const pinnedRes = await fetchWithRetry({
          method: "get",
          url: `/api/chat/pinned-messages/${conversationId}`,
        });
        setPinnedMessages((prev) => ({
          ...prev,
          [targetUserId]: pinnedRes.data.map((pin) => pin.messageId),
        }));
        localStorage.setItem(`pinnedMessages_${conversationId}`, JSON.stringify(pinnedRes.data.map((pin) => pin.messageId)));
        const savedLimit = localStorage.getItem("recallTimeLimit");
        if (savedLimit) {
          setRecallTimeLimit(parseInt(savedLimit));
        }
      } catch (err) {
        let errorMessage = "Không thể tải lịch sử tin nhắn!";
        if (err.response) {
          if (err.response.status === 403) {
            errorMessage = err.response.data.message || "Bạn không có quyền truy cập lịch sử tin nhắn!";
          } else if (err.response.status === 404) {
            errorMessage = "Không tìm thấy người nhận!";
          } else if (err.response.status === 401) {
            errorMessage = "Phiên làm việc hết hạn. Vui lòng đăng nhập lại.";
          } else if (err.response.status === 500) {
            errorMessage = "Server gặp sự cố, vui lòng thử lại sau.";
          } else {
            errorMessage = err.response.data?.message || "Không thể tải lịch sử tin nhắn!";
          }
        } else if (err.message.includes("Network Error")) {
          errorMessage = "Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng và thử lại!";
        }
        showNotification(errorMessage, "error");
      }
    },
    [fetchWithRetry, showNotification, authUser]
  );

  useEffect(() => {
    if (!onSelectChat || !authUser || !authUser._id) return;
    if (!onSelectChat || typeof onSelectChat !== "string" || onSelectChat.includes("friendsList")) {
      console.warn(`ID phiên chat không hợp lệ: ${onSelectChat}`);
      return;
    }
    if (!chatSessions.includes(onSelectChat)) {
      setChatSessions((prev) => [onSelectChat, ...prev.filter((id) => id !== onSelectChat)]);
      refreshChatHistory(onSelectChat);
    }
  }, [onSelectChat, authUser, chatSessions, refreshChatHistory]);

  useEffect(() => {
    if (!authUser || !token) return;

    socketRef.current = getSocket();
    if (!socketRef.current) {
      console.warn("Socket chưa sẵn sàng, khởi tạo socket...");
      socketRef.current = initializeSocket(token);
      if (!socketRef.current) {
        showNotification("Không thể kết nối tới server thông báo.", "error");
        return;
      }
    }

    socketRef.current.on("connect", () => {
      setIsSocketConnected(true);
      console.log("Socket.IO đã kết nối thành công");
      chatSessions.forEach((targetUserId) => {
        if (targetUserId && typeof targetUserId === "string" && !targetUserId.includes("friendsList")) {
          socketRef.current.emit("joinChatRoom", { userId: authUser._id, targetUserId });
          socketRef.current.on("joinedRoom", async ({ roomId: joinedRoomId }) => {
            console.log(`Client ${authUser._id} đã tham gia room ${joinedRoomId}`);
            await refreshChatHistory(targetUserId);
          });
        }
      });
    });

    socketRef.current.on("connect_error", (err) => {
      setIsSocketConnected(false);
      showNotification("Không thể kết nối tới server thông báo. Đang thử lại...", "error");
      console.error("Socket.IO connect error:", err);
    });

    socketRef.current.on("disconnect", (reason) => {
      setIsSocketConnected(false);
      console.log("Socket.IO disconnected:", reason);
      if (reason !== "io client disconnect") {
        setTimeout(() => {
          socketRef.current.connect();
        }, 3000);
      }
    });

    socketRef.current.on("auth_error", async ({ message }) => {
      console.error("Lỗi xác thực Socket.IO:", message);
      try {
        const newToken = await fetchWithRetry({
          method: "post",
          url: "/api/users/auth/refresh-token",
          data: { refreshToken: localStorage.getItem("refreshToken") },
        });
        setToken(newToken.data.accessToken);
        socketRef.current.auth.token = `Bearer ${newToken.data.accessToken}`;
        socketRef.current.connect();
      } catch (error) {
        showNotification("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.", "error");
        navigate("/login");
      }
    });

    socketRef.current.on("chatMessage", (message) => {
      const targetUserId =
        message.senderId === authUser._id ? message.receiverId : message.senderId;
      const targetUser = getUserInfo(targetUserId);

      if (message.senderId === authUser._id && message._id === pendingMessageIdRef.current) {
        console.log(`Bỏ qua tin nhắn với _id ${message._id} vì đã xử lý trong handleSendMessage`);
        return;
      }

      const isRelevantMessage =
        (message.senderId === authUser._id && message.receiverId === targetUserId) ||
        (message.senderId === targetUserId && message.receiverId === authUser._id) ||
        (message.isSystemMessage &&
          ((message.originalSenderId === authUser._id && message.originalReceiverId === targetUserId) ||
           (message.originalSenderId === targetUserId && message.originalReceiverId === authUser._id)));

      if (!isRelevantMessage) {
        console.log("Bỏ qua tin nhắn không thuộc cuộc trò chuyện hiện tại:", message);
        return;
      }

      const formattedMessage = {
        ...message,
        createdAt: new Date(message.createdAt),
        readTimestamp: message.readTimestamp ? new Date(message.readTimestamp) : null,
        isTemp: false,
        liked: message.liked || false,
        replyTo: message.replyTo || null,
        isSystemMessage: message.isSystemMessage || false,
        isGift: message.isGift || false,
        gift: message.gift || null,
        originalMessageId: message.originalMessageId || null,
        originalSenderId: message.originalSenderId || null,
        originalReceiverId: message.originalReceiverId || null,
      };

      setMessages((prev) => {
        const currentMessages = prev[targetUserId] || [];
        const existingMessageIndex = currentMessages.findIndex(
          (msg) =>
            msg._id === message._id ||
            (msg.isSystemMessage && msg.originalMessageId && msg.originalMessageId === message.originalMessageId)
        );

        if (existingMessageIndex !== -1) {
          const updatedMessages = [...currentMessages];
          updatedMessages[existingMessageIndex] = formattedMessage;
          return {
            ...prev,
            [targetUserId]: updatedMessages,
          };
        }

        return {
          ...prev,
          [targetUserId]: [...currentMessages, formattedMessage],
        };
      });

      if (!(message.isSystemMessage || message.isHidden)) {
        showNotification(
          `Tin nhắn mới từ ${targetUser?.name || "Người dùng"}: ${message.isGift ? "[GIFT] " + message.gift?.name : message.content}`,
          "info"
        );
      }
    });

    socketRef.current.on("messageRead", (message) => {
      setMessages((prev) => ({
        ...prev,
        [activeChatUserId]: prev[activeChatUserId]?.map((msg) =>
          msg._id === message._id
            ? {
                ...msg,
                read: true,
                status: "read",
                readTimestamp: new Date(message.readTimestamp),
              }
            : msg
        ),
      }));
    });

    socketRef.current.on("messageLiked", ({ _id, liked }) => {
      setMessages((prev) => ({
        ...prev,
        [activeChatUserId]: prev[activeChatUserId]?.map((msg) =>
          msg._id === _id ? { ...msg, liked } : msg
        ),
      }));
    });

    socketRef.current.on("messageRecalled", ({ messageId, recalledMessage }) => {
      setMessages((prev) => ({
        ...prev,
        [activeChatUserId]: prev[activeChatUserId]?.map((msg) =>
          msg._id === messageId ? recalledMessage : msg
        ),
      }));
      setPinnedMessages((prev) => ({
        ...prev,
        [activeChatUserId]: prev[activeChatUserId]?.filter((id) => id !== messageId) || [],
      }));
      setMarkedMessages((prev) => ({
        ...prev,
        [activeChatUserId]: prev[activeChatUserId]?.filter((id) => id !== messageId) || [],
      }));
    });

    socketRef.current.on("messagesDeleted", ({ messageIds }) => {
      setMessages((prev) => ({
        ...prev,
        [activeChatUserId]: prev[activeChatUserId]?.filter((msg) => !messageIds.includes(msg._id)),
      }));
      setPinnedMessages((prev) => ({
        ...prev,
        [activeChatUserId]: prev[activeChatUserId]?.filter((id) => !messageIds.includes(id)) || [],
      }));
      setMarkedMessages((prev) => ({
        ...prev,
        [activeChatUserId]: prev[activeChatUserId]?.filter((id) => !messageIds.includes(id)) || [],
      }));
      setSelectedMessages((prev) => prev.filter((id) => !messageIds.includes(id)));
    });

    socketRef.current.on("allMessagesDeleted", ({ userId, targetUserId }) => {
      if (
        (userId === authUser._id && targetUserId === activeChatUserId) ||
        (targetUserId === authUser._id && userId === activeChatUserId)
      ) {
        setMessages((prev) => ({
          ...prev,
          [activeChatUserId]: [],
        }));
        setPinnedMessages((prev) => ({ ...prev, [activeChatUserId]: [] }));
        setMarkedMessages((prev) => ({ ...prev, [activeChatUserId]: [] }));
        setSelectedMessages([]);
      }
    });

    socketRef.current.on("pinnedMessagesUpdated", ({ conversationId, pinnedMessages: updatedPinned }) => {
      const expectedConversationId = [authUser._id, activeChatUserId].sort().join("_");
      if (conversationId === expectedConversationId) {
        setPinnedMessages((prev) => ({
          ...prev,
          [activeChatUserId]: updatedPinned.map((pin) => pin.messageId),
        }));
      }
    });

    socketRef.current.on("notification", (notificationData) => {
      if (notificationData.type === "pin_limit_reached") {
        showNotification(notificationData.message, "warning");
      } else if (
        notificationData.type === "chat_restriction_updated" ||
        notificationData.type === "hide_messages_toggled" ||
        notificationData.type === "hide_messages_with_user_toggled"
      ) {
        refreshChatHistory(activeChatUserId);
        showNotification(notificationData.message, "info");
      } else if (notificationData.type === "gift" && notificationData.userId === activeChatUserId) {
        const giftMessage = {
          _id: `gift-${Date.now()}`,
          senderId: activeChatUserId,
          receiverId: authUser._id,
          content: "[GIFT]",
          createdAt: new Date().toISOString(),
          isSystemMessage: false,
          isGift: true,
          gift: {
            id: notificationData.giftId,
            name: notificationData.message.split("một ")[1].split("!")[0],
            imageUrl: notificationData.giftImageUrl,
            coinPrice: notificationData.giftCoinPrice,
          },
        };
        setMessages((prev) => ({
          ...prev,
          [activeChatUserId]: [...(prev[activeChatUserId] || []), giftMessage],
        }));
        showNotification(notificationData.message, "info");
      } else {
        showNotification(
          `${notificationData.message} ❤️`,
          notificationData.type === "like" ? "info" : "warning"
        );
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off("connect");
        socketRef.current.off("connect_error");
        socketRef.current.off("disconnect");
        socketRef.current.off("auth_error");
        socketRef.current.off("chatMessage");
        socketRef.current.off("messageRead");
        socketRef.current.off("messageLiked");
        socketRef.current.off("messageRecalled");
        socketRef.current.off("messagesDeleted");
        socketRef.current.off("allMessagesDeleted");
        socketRef.current.off("pinnedMessagesUpdated");
        socketRef.current.off("notification");
      }
    };
  }, [authUser, token, setToken, chatSessions, activeChatUserId, getUserInfo, showNotification, navigate, fetchWithRetry, refreshChatHistory]);

  useEffect(() => {
    if (!authUser || !activeChatUserId) return;

    const socket = getSocket();
    if (!socket) {
      console.warn("Socket chưa sẵn sàng, bỏ qua đánh dấu tin nhắn đã đọc");
      return;
    }

    const currentMessages = messages[activeChatUserId] || [];
    const unreadMessages = currentMessages.filter(
      (msg) => msg.senderId === activeChatUserId && msg.receiverId === authUser._id && !msg.read && !msg.isSystemMessage
    );

    unreadMessages.forEach((msg) => {
      socket.emit("markMessageAsRead", msg._id);
    });
  }, [messages, authUser, activeChatUserId, token]);

  const handleSendMessage = useCallback(
    async () => {
      if (!messageInput.trim()) {
        console.log("Tin nhắn trống, không gửi.");
        return;
      }

      if (!authUser?._id || !activeChatUserId) {
        console.error("Dữ liệu người dùng không hợp lệ:", { authUser, activeChatUserId });
        showNotification("Dữ liệu người dùng không hợp lệ!", "error");
        return;
      }

      const tempMessageId = `temp-${Date.now()}`;
      const tempMessage = {
        _id: tempMessageId,
        senderId: authUser._id,
        receiverId: activeChatUserId,
        content: messageInput,
        createdAt: new Date(),
        read: false,
        status: "sending",
        isTemp: true,
        liked: false,
        replyTo: replyToMessage ? replyToMessage._id : null,
      };

      setMessages((prev) => ({
        ...prev,
        [activeChatUserId]: [...(prev[activeChatUserId] || []), tempMessage],
      }));
      setMessageInput("");
      setReplyToMessage(null);

      const messageData = {
        senderId: authUser._id,
        receiverId: activeChatUserId,
        content: messageInput,
        replyTo: replyToMessage ? replyToMessage._id : null,
      };

      try {
        let socket = getSocket();
        if (!socket && token) {
          console.log("Socket chưa sẵn sàng, khởi tạo...");
          socket = initializeSocket(token);
          await new Promise((resolve) => {
            socket.on("connect", () => {
              console.log("Socket khởi tạo thành công!");
              setIsSocketConnected(true);
              resolve();
            });
            socket.on("connect_error", (err) => {
              console.warn("Không thể khởi tạo socket:", err.message);
              setIsSocketConnected(false);
              resolve();
            });
            setTimeout(resolve, 2000);
          });
        }

        if (socket && socket.connected) {
          socket.emit("sendMessage", messageData, (response) => {
            if (response?.error) {
              console.error("Lỗi từ server khi gửi tin nhắn qua Socket.IO:", response.error);
              const errorMessage =
                response.error.status === 500
                  ? "Server gặp sự cố, vui lòng thử lại sau."
                  : response.error.message || "Không thể gửi tin nhắn, vui lòng thử lại!";
              showNotification(errorMessage, "error");
              setMessages((prev) => ({
                ...prev,
                [activeChatUserId]: prev[activeChatUserId].filter((msg) => msg._id !== tempMessageId),
              }));
            } else {
              console.log("Tin nhắn gửi thành công qua Socket.IO:", response.data);
              pendingMessageIdRef.current = response.data._id;
              setTimeout(() => {
                pendingMessageIdRef.current = null;
              }, 2000);
            }
          });
        } else {
          console.warn("Socket.IO không kết nối, gửi tin nhắn qua API trực tiếp...");
          const response = await fetchWithRetry({
            method: "post",
            url: "/api/chat/send",
            data: messageData,
          });
          pendingMessageIdRef.current = response.data._id;
          setTimeout(() => {
            pendingMessageIdRef.current = null;
          }, 2000);
        }
      } catch (error) {
        console.error("Lỗi khi gửi tin nhắn:", error);
        const errorMessage =
          error.message === "Network Error"
            ? "Không thể kết nối tới server, vui lòng kiểm tra mạng."
            : "Không thể gửi tin nhắn, vui lòng thử lại!";
        showNotification(errorMessage, "error");
        setMessages((prev) => ({
          ...prev,
          [activeChatUserId]: prev[activeChatUserId].filter((msg) => msg._id !== tempMessageId),
        }));
      }
    },
    [messageInput, authUser, activeChatUserId, replyToMessage, fetchWithRetry, showNotification, token]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      } else if (e.key === "Enter" && e.shiftKey) {
        setMessageInput((prev) => prev + "\n");
      } else if (e.key === "Delete" && selectedMessages.length > 0) {
        handleDeleteSelectedMessages();
      }
    },
    [handleSendMessage, handleDeleteSelectedMessages, selectedMessages]
  );

  const getLastSenderMessageIndex = useCallback(() => {
    const currentMessages = messages[activeChatUserId] || [];
    for (let i = currentMessages.length - 1; i >= 0; i--) {
      if (currentMessages[i].senderId === authUser._id) {
        return currentMessages[i]._id;
      }
    }
    return null;
  }, [messages, activeChatUserId, authUser]);

  const lastSenderMessageId = getLastSenderMessageIndex();

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeChatUserId]);

  if (!authUser) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50" onClick={closeContextMenu}>
      {notification && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 text-white ${
            isNotiFading ? "animate-fade-out" : "animate-slide-in"
          } ${
            notification.type === "success"
              ? "bg-green-500"
              : notification.type === "warning"
              ? "bg-yellow-500"
              : notification.type === "info"
              ? "bg-blue-500"
              : "bg-red-500"
          }`}
        >
          {notification.message}
          {notification.message.includes("Phiên làm việc hết hạn") && (
            <button
              onClick={() => navigate("/login")}
              className="ml-2 px-2 py-1 bg-white text-red-500 rounded hover:bg-gray-200"
            >
              Đăng nhập lại
            </button>
          )}
        </div>
      )}

      <div className="bg-transparent p-2 w-72 flex flex-col space-y-2 max-h-60 overflow-y-auto">
        {chatSessions.map((userId) => {
          const user = getUserInfo(userId);
          return (
            <div
              key={userId}
              onClick={() => setActiveChatUserId(userId)}
              onContextMenu={(e) => handleSessionContextMenu(e, userId)}
              className="flex items-center justify-between bg-green-200 text-black rounded-lg px-4 py-2 cursor-pointer hover:bg-green-300 transition duration-200"
            >
              <div className="flex items-center">
                <Link to={`/profile/${user?.membershipId || user._id}`}>
                  <img
                    src={getAvatarUrl(user)}
                    alt={user?.name || "User Avatar"}
                    className="w-10 h-10 rounded-full mr-2 border-2 border-green-300 shadow-md"
                    onError={(e) => (e.target.src = "https://placehold.co/50")}
                  />
                </Link>
                <div>
                  <span className="block truncate max-w-[120px] font-medium flex items-center">
                    {user?.name || user?.email || "Không xác định"}
                    {(user?.role === "vip_gold" ||
                      user?.role === "vip_platinum" ||
                      user?.role === "vip_diamond") && (
                      <FaCrown className={`ml-2 ${getVipIconColor(user.role)}`} />
                    )}
                  </span>
                  <span className="text-xs text-gray-700">{user?.status || "N/A"}</span>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseChatSession(userId);
                }}
                className="text-black hover:text-red-500 transition duration-200"
              >
                <FaTimes size={20} />
              </button>
            </div>
          );
        })}
      </div>

      {activeChatUserId && (
        <div className="fixed bottom-4 right-80 w-80 bg-white rounded-lg shadow-lg">
          <div className="flex items-center justify-between bg-green-200 text-black rounded-t-lg p-3">
            <div className="flex items-center">
              <Link to={`/profile/${getUserInfo(activeChatUserId)?.membershipId || activeChatUserId}`}>
                <img
                  src={getAvatarUrl(getUserInfo(activeChatUserId))}
                  alt={getUserInfo(activeChatUserId)?.name || "User Avatar"}
                  className="w-10 h-10 rounded-full mr-2 border-2 border-green-300 shadow-md"
                  onError={(e) => (e.target.src = "https://placehold.co/50")}
                />
              </Link>
              <div>
                <h3 className="text-lg font-semibold flex items-center">
                  {getUserInfo(activeChatUserId)?.name || "Người dùng"}
                  {(getUserInfo(activeChatUserId)?.role === "vip_gold" ||
                    getUserInfo(activeChatUserId)?.role === "vip_platinum" ||
                    getUserInfo(activeChatUserId)?.role === "vip_diamond") && (
                    <FaCrown className={`ml-2 ${getVipIconColor(getUserInfo(activeChatUserId).role)}`} />
                  )}
                </h3>
                <span className="text-xs text-gray-700">{isSocketConnected ? "Trực tuyến" : "Ngoại tuyến"}</span>
              </div>
            </div>
            <button
              onClick={() => setActiveChatUserId(null)}
              className="text-black hover:text-red-500 transition duration-200"
            >
              <FaTimes size={24} />
            </button>
          </div>
          <div className="h-80 overflow-y-auto p-4 bg-gray-50">
            {pinnedMessages[activeChatUserId]?.length > 0 && (
              <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Tin nhắn được ghim</h3>
                {messages[activeChatUserId]
                  ?.filter((msg) => pinnedMessages[activeChatUserId]?.includes(msg._id))
                  .map((msg) => (
                    <div
                      key={msg._id}
                      className="p-2 bg-yellow-100 rounded mb-2"
                      onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                    >
                      <p className="text-gray-800" style={{ whiteSpace: "pre-line" }}>
                        {msg.content}
                      </p>
                      <p className="text-xs text-gray-500">{new Date(msg.createdAt).toLocaleTimeString()}</p>
                    </div>
                  ))}
              </div>
            )}
            {replyToMessage && (
              <div className="mb-2 p-2 bg-gray-200 rounded flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Đang trả lời: {replyToMessage.content}</p>
                </div>
                <button
                  onClick={() => setReplyToMessage(null)}
                  className="text-red-500 hover:text-red-700"
                >
                  Hủy
                </button>
              </div>
            )}
            {messages[activeChatUserId]?.map((msg) => {
  // Xử lý createdAt: nếu là chuỗi ISO, chuyển thành đối tượng Date
  const parsedCreatedAt = typeof msg.createdAt === "string" ? new Date(msg.createdAt) : msg.createdAt;
  const isValidCreatedAt = parsedCreatedAt instanceof Date && !isNaN(parsedCreatedAt);
  const createdAtTime = isValidCreatedAt
    ? `${parsedCreatedAt.getDate()} tháng ${parsedCreatedAt.getMonth() + 1} năm ${parsedCreatedAt.getFullYear()} ${parsedCreatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : "Không xác định";

  const isValidReadTimestamp = msg.readTimestamp instanceof Date && !isNaN(msg.readTimestamp);
  const readTimestampTime = isValidReadTimestamp
    ? `${msg.readTimestamp.getDate()} thg ${msg.readTimestamp.getMonth() + 1} năm ${msg.readTimestamp.getFullYear()} ${msg.readTimestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : msg.read ? "Chưa có thời gian" : "Chưa xem";

  const receiverAvatar = getUserInfo(activeChatUserId);
  const lastReceivedMessageId = getLastReceivedMessageId(activeChatUserId);
  const showLikeButton =
    (msg._id === lastReceivedMessageId ||
      (msg.isSystemMessage && msg.originalMessageId === lastReceivedMessageId)) &&
    msg.senderId !== authUser._id;

  const isSelected = selectedMessages.includes(msg._id);
  const isExpanded = expandedMessages[msg._id] || false;
  const maxLines = 5;
  const lines = msg.content.split("\n");
  const displayContent = isExpanded || lines.length <= maxLines ? msg.content : lines.slice(0, maxLines).join("\n") + "...";

  const toggleExpand = () => {
    setExpandedMessages((prev) => ({
      ...prev,
      [msg._id]: !isExpanded,
    }));
  };

  const shouldShowSystemMessage = msg.isSystemMessage;
  const isCurrentUserUserRole = authUser.role === "user";
  const isGiftMessage = msg.isGift;

  let adjustedDisplayContent = displayContent;
  if (shouldShowSystemMessage) {
    if (msg.originalSenderId === authUser._id) {
      adjustedDisplayContent = `${getUserInfo(activeChatUserId)?.name || "Người nhận"} Không thể đọc tin nhắn ngay bây giờ vì bạn là hội viên thường MIỄN PHÍ.`;
    } else if (msg.originalReceiverId === authUser._id) {
      adjustedDisplayContent = `Bạn có tin nhắn từ ${msg.originalSenderId === activeChatUserId ? getUserInfo(activeChatUserId).name : "Người gửi"} nhưng chưa thể đọc tin nhắn ngay bây giờ vì bạn là hội viên thường MIỄN PHÍ.`;
    }
  }

  return (
    <div
      key={msg._id}
      className={`mb-2 flex relative ${
        (msg.senderId === authUser._id || (msg.isSystemMessage && msg.originalSenderId === authUser._id))
          ? "justify-end"
          : "justify-start"
      } ${markedMessages[activeChatUserId]?.includes(msg._id) ? "bg-yellow-50 p-2 rounded" : ""} ${
        isSelected ? "bg-blue-100 border border-blue-500" : ""
      }`}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey) {
          setSelectedMessages((prev) =>
            prev.includes(msg._id) ? prev.filter((id) => id !== msg._id) : [...prev, msg._id]
          );
        }
      }}
      onContextMenu={(e) => handleMessageContextMenu(e, msg)}
    >
      <div className="flex items-start">
        <div
          className={`${
            (msg.senderId === authUser._id || (msg.isSystemMessage && msg.originalSenderId === authUser._id))
              ? "text-right"
              : "text-left"
          }`}
        >
          {shouldShowSystemMessage ? (
            (msg.senderId === authUser._id || (msg.isSystemMessage && msg.originalSenderId === authUser._id)) ? (
              <div className="flex flex-col items-end">
                <span className="text-xs text-gray-500 mb-1 self-end">{isValidCreatedAt ? createdAtTime : "Không xác định"}</span>
                <div
                  className="relative w-fit max-w-xs p-2 bg-yellow-100 text-gray-800 rounded-lg shadow-md"
                  onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                >
                  <div style={{ whiteSpace: "pre-line" }}>
                    {adjustedDisplayContent}
                    {isCurrentUserUserRole && adjustedDisplayContent !== "Tin nhắn đã được thu hồi" && (
                      <button
                        onClick={() => navigate("/upgrade")}
                        className="ml-2 text-blue-500 underline hover:text-blue-700"
                      >
                        Nâng cấp ngay
                      </button>
                    )}
                  </div>
                  {lines.length > maxLines && (
                    <button onClick={toggleExpand} className="text-blue-500 text-xs mt-1">
                      {isExpanded ? "Thu gọn" : "Xem thêm"}
                    </button>
                  )}
                  {showLikeButton && (
                    <div className="absolute bottom-1 right-1">
                      {msg.liked ? (
                        <FaHeart
                          className="text-red-500 cursor-pointer"
                          size={12}
                          onClick={() => handleLikeMessage(msg.originalMessageId || msg._id)}
                        />
                      ) : (
                        <FaHeart
                          className="text-gray-400 cursor-pointer"
                          size={12}
                          onClick={() => handleLikeMessage(msg.originalMessageId || msg._id)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 mb-1 self-start">{isValidCreatedAt ? createdAtTime : "Không xác định"}</span>
                <div className="flex items-start">
                  <Link to={`/profile/${receiverAvatar?.membershipId || receiverAvatar?._id}`}>
                    <img
                      src={getAvatarUrl(receiverAvatar)}
                      alt={receiverAvatar?.name || "Sender Avatar"}
                      className="w-8 h-8 rounded-full object-cover mr-2 border-2 border-green-300 shadow-md"
                      onError={(e) => (e.target.src = "https://placehold.co/50")}
                    />
                  </Link>
                  <div
                    className="relative w-fit max-w-xs p-2 bg-white rounded-lg shadow-md border border-gray-200"
                    onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                  >
                    {msg.replyTo && (
                      <div className="text-xs text-gray-500 mb-1 italic">
                        Trả lời: {messages[activeChatUserId]?.find((m) => m._id === msg.replyTo)?.content}
                      </div>
                    )}
                    <div className="text-gray-800" style={{ whiteSpace: "pre-line" }}>
                      {adjustedDisplayContent}
                    </div>
                    {lines.length > maxLines && (
                      <button onClick={toggleExpand} className="text-blue-500 text-xs mt-1">
                        {isExpanded ? "Thu gọn" : "Xem thêm"}
                      </button>
                    )}
                    {isCurrentUserUserRole && adjustedDisplayContent !== "Tin nhắn đã được thu hồi" && (
                      <button
                        onClick={() => navigate("/upgrade")}
                        className="ml-2 text-blue-500 underline hover:text-blue-700"
                      >
                        Nâng cấp ngay
                      </button>
                    )}
                    {showLikeButton && (
                      <div className="absolute bottom-1 right-1">
                        {msg.liked ? (
                          <FaHeart
                            className="text-red-500 cursor-pointer"
                            size={12}
                            onClick={() => handleLikeMessage(msg.originalMessageId || msg._id)}
                          />
                        ) : (
                          <FaHeart
                            className="text-gray-400 cursor-pointer"
                            size={12}
                            onClick={() => handleLikeMessage(msg.originalMessageId || msg._id)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          ) : isGiftMessage ? (
            msg.senderId !== authUser._id ? (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 mb-1 self-start">{isValidCreatedAt ? createdAtTime : "Không xác định"}</span>
                <div className="flex items-start">
                  <Link to={`/profile/${receiverAvatar?.membershipId || receiverAvatar?._id}`}>
                    <img
                      src={getAvatarUrl(receiverAvatar)}
                      alt={receiverAvatar?.name || "Sender Avatar"}
                      className="w-8 h-8 rounded-full object-cover mr-2 border-2 border-green-300 shadow-md"
                      onError={(e) => (e.target.src = "https://placehold.co/50")}
                    />
                  </Link>
                  <div
                    className="relative w-fit max-w-xs p-2 bg-white rounded-lg shadow-md border border-gray-200"
                    onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                  >
                    <div className="text-gray-800">
                      <img
                        src={msg.gift.imageUrl}
                        alt={msg.gift.name}
                        className="w-24 h-24 object-cover rounded"
                        onError={(e) => (e.target.src = "https://placehold.co/50x50")}
                      />
                      <p className="text-xs text-gray-500 mt-1">{msg.gift.name}</p>
                    </div>
                    {showLikeButton && (
                      <div className="absolute bottom-1 right-1">
                        {msg.liked ? (
                          <FaHeart
                            className="text-red-500 cursor-pointer"
                            size={12}
                            onClick={() => handleLikeMessage(msg._id)}
                          />
                        ) : (
                          <FaHeart
                            className="text-gray-400 cursor-pointer"
                            size={12}
                            onClick={() => handleLikeMessage(msg._id)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-end">
                <span className="text-xs text-gray-500 mb-1 self-end">{isValidCreatedAt ? createdAtTime : "Không xác định"}</span>
                <div
                  className="relative w-fit max-w-xs p-2 bg-green-500 text-white rounded-lg shadow-md"
                  onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                >
                  <div>
                    <img
                      src={msg.gift.imageUrl}
                      alt={msg.gift.name}
                      className="w-24 h-24 object-cover rounded"
                      onError={(e) => (e.target.src = "https://placehold.co/50x50")}
                    />
                    <p className="text-xs mt-1">{msg.gift.name}</p>
                  </div>
                  {msg.liked && (
                    <div className="absolute bottom-1 right-1">
                      <FaHeart className="text-red-500" size={12} />
                    </div>
                  )}
                </div>
                {msg._id === lastSenderMessageId && msg.read && (
                  <div className="flex items-end mt-1">
                    <span className="text-xs text-gray-500 mr-1">
                      Đã xem {isValidReadTimestamp ? `lúc ${readTimestampTime}` : ""}
                    </span>
                    <Link to={`/profile/${receiverAvatar?.membershipId || receiverAvatar?._id}`}>
                      <img
                        src={getAvatarUrl(receiverAvatar)}
                        alt={receiverAvatar?.name || "Receiver Avatar"}
                        className="w-6 h-6 rounded-full object-cover border border-green-300 shadow-md"
                        onError={(e) => (e.target.src = "https://placehold.co/50")}
                      />
                    </Link>
                  </div>
                )}
              </div>
            )
          ) : (
            (msg.senderId === authUser._id || (msg.isSystemMessage && msg.originalSenderId === authUser._id)) ? (
              <div className="flex flex-col items-end">
                <span className="text-xs text-gray-500 mb-1 self-end">{isValidCreatedAt ? createdAtTime : "Không xác định"}</span>
                <div className="relative w-fit max-w-xs p-2 bg-green-500 text-white rounded-lg shadow-md">
                  {msg.replyTo && (
                    <div className="text-xs text-gray-200 mb-1 italic">
                      Trả lời: {messages[activeChatUserId]?.find((m) => m._id === msg.replyTo)?.content}
                    </div>
                  )}
                  <div style={{ whiteSpace: "pre-line" }}>{displayContent}</div>
                  {lines.length > maxLines && (
                    <button onClick={toggleExpand} className="text-blue-200 text-xs mt-1">
                      {isExpanded ? "Thu gọn" : "Xem thêm"}
                    </button>
                  )}
                  {msg.liked && (
                    <div className="absolute bottom-1 right-1">
                      <FaHeart className="text-red-500" size={12} />
                    </div>
                  )}
                </div>
                {msg._id === lastSenderMessageId && msg.read && (
                  <div className="flex items-end mt-1">
                    <span className="text-xs text-gray-500 mr-1">
                      Đã xem {isValidReadTimestamp ? `lúc ${readTimestampTime}` : ""}
                    </span>
                    <Link to={`/profile/${receiverAvatar?.membershipId || receiverAvatar?._id}`}>
                      <img
                        src={getAvatarUrl(receiverAvatar)}
                        alt={receiverAvatar?.name || "Receiver Avatar"}
                        className="w-6 h-6 rounded-full object-cover border border-green-300 shadow-md"
                        onError={(e) => (e.target.src = "https://placehold.co/50")}
                      />
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 mb-1 self-start">{isValidCreatedAt ? createdAtTime : "Không xác định"}</span>
                <div className="flex items-start">
                  <Link to={`/profile/${receiverAvatar?.membershipId || receiverAvatar?._id}`}>
                    <img
                      src={getAvatarUrl(receiverAvatar)}
                      alt={receiverAvatar?.name || "Sender Avatar"}
                      className="w-8 h-8 rounded-full object-cover mr-2 border-2 border-green-300 shadow-md"
                      onError={(e) => (e.target.src = "https://placehold.co/50")}
                    />
                  </Link>
                  <div className="relative w-fit max-w-xs p-2 bg-white rounded-lg shadow-md border border-gray-200">
                    {msg.replyTo && (
                      <div className="text-xs text-gray-500 mb-1 italic">
                        Trả lời: {messages[activeChatUserId]?.find((m) => m._id === msg.replyTo)?.content}
                      </div>
                    )}
                    <div className="text-gray-800" style={{ whiteSpace: "pre-line" }}>
                      {displayContent}
                    </div>
                    {lines.length > maxLines && (
                      <button onClick={toggleExpand} className="text-blue-500 text-xs mt-1">
                        {isExpanded ? "Thu gọn" : "Xem thêm"}
                      </button>
                    )}
                    {showLikeButton && (
                      <div className="absolute bottom-1 right-1">
                        {msg.liked ? (
                          <FaHeart
                            className="text-red-500 cursor-pointer"
                            size={12}
                            onClick={() => handleLikeMessage(msg._id)}
                          />
                        ) : (
                          <FaHeart
                            className="text-gray-400 cursor-pointer"
                            size={12}
                            onClick={() => handleLikeMessage(msg._id)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
})}
            <div ref={chatEndRef} />
          </div>
          <div className="p-3 border-t flex items-center">
            <button
              onClick={() => setShowGiftModal(true)}
              className="p-3 bg-purple-500 text-white rounded-l-lg hover:bg-purple-600 transition duration-300"
              title="Gửi quà tặng"
            >
              <FaGift />
            </button>
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 border rounded-none p-2 focus:outline-none focus:ring-2 focus:ring-green-400 resize-none h-12"
              placeholder={authUser.role === "user" ? "Nhập tin nhắn..." : "Nhập tin nhắn..."}
            />
            <ChatRestriction
              userId={activeChatUserId}
              token={token}
              messages={messages[activeChatUserId] || []}
              onSendMessage={handleSendMessage}
              authUser={authUser}
              targetUser={getUserInfo(activeChatUserId)}
              setMessages={(newMessages) =>
                setMessages((prev) => ({ ...prev, [activeChatUserId]: newMessages }))
              }
              newMessage={messageInput}
              fetchWithRetry={fetchWithRetry}
            />
          </div>
          {activeChatUserId && showGiftModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
                <h3 className="text-lg font-semibold mb-4">Chọn quà tặng</h3>
                <SendGift
                  selectedUserId={{ id: activeChatUserId }}
                  onClose={() => setShowGiftModal(false)}
                  onGiftSent={handleSendGift}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <AnimatePresence>
  {contextMenu && (
    contextMenu.type === "session" ? (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="fixed bg-white shadow-lg rounded-lg p-2 z-50 border border-gray-200"
        style={{
          bottom: window.innerHeight - contextMenu.y,
          left: contextMenu.x,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
          onClick={() => handleOpenChatWindow(contextMenu.userId)}
        >
          <FaComments className="mr-2" /> Mở cửa sổ chat
        </div>
        <div
          className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
          onClick={() => handleOpenChatPage(contextMenu.userId)}
        >
          <FaExternalLinkAlt className="mr-2" /> Mở trong trang chat
        </div>
        <div
          className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
          onClick={() => handleDeleteAllMessages(contextMenu.userId)}
        >
          <FaTrash className="mr-2" /> Xóa tất cả tin nhắn
        </div>
        <div
          className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
          onClick={() => handleCloseChatSession(contextMenu.userId)}
        >
          <FaTimes className="mr-2" /> Đóng phiên chat
        </div>
      </motion.div>
    ) : (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        ref={(node) => {
          if (node) {
            const menuWidth = node.offsetWidth;
            const menuHeight = node.offsetHeight;
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            let adjustedX = contextMenu.x;
            let adjustedY = contextMenu.y;

            if (contextMenu.x + menuWidth > windowWidth) {
              adjustedX = windowWidth - menuWidth - 10;
            }

            if (contextMenu.y + menuHeight > windowHeight) {
              adjustedY = windowHeight - menuHeight - 10;
            }

            node.style.left = `${adjustedX}px`;
            node.style.top = `${adjustedY}px`;
          }
        }}
        className="fixed bg-white shadow-lg rounded-lg p-2 z-50 border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {selectedMessages.length > 0 ? (
          <>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={handleDeleteSelectedMessages}
            >
              <FaTrash className="mr-2" /> Xóa {selectedMessages.length} tin nhắn đã chọn
            </div>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={handleShareSelectedMessages}
            >
              <FaShare className="mr-2" /> Chia sẻ các tin nhắn đã chọn
            </div>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer text-gray-500"
              onClick={() => setSelectedMessages([])}
            >
              <span>Hủy chọn</span>
            </div>
          </>
        ) : (
          <>
            <div className="text-gray-500 text-xs p-2 italic">Nhấn Ctrl + Click để chọn nhiều tin nhắn</div>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleLikeMessage(contextMenu.messageId)}
            >
              <FaHeart className="mr-2" /> Like
            </div>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleDeleteMessage(contextMenu.messageId)}
            >
              <FaTrash className="mr-2" /> Xóa tin nhắn
            </div>
            {messages[activeChatUserId]?.find((msg) => msg._id === contextMenu.messageId)?.senderId === authUser._id && (
              <div
                className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleRecallMessage(contextMenu.messageId)}
              >
                <FaUndo className="mr-2" /> Thu hồi tin nhắn
              </div>
            )}
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleDeleteAllMessages(activeChatUserId)}
            >
              <FaTrash className="mr-2" /> Xóa tất cả tin nhắn
            </div>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleCopyMessage(contextMenu.messageContent)}
            >
              <FaCopy className="mr-2" /> Sao chép
            </div>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleReplyMessage(contextMenu.messageId)}
            >
              <FaReply className="mr-2" /> Trả lời
            </div>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleShareMessage(contextMenu.messageId)}
            >
              <FaShare className="mr-2" /> Chia sẻ
            </div>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handlePinMessage(contextMenu.messageId)}
            >
              <FaThumbtack className="mr-2" />{" "}
              {pinnedMessages[activeChatUserId]?.includes(contextMenu.messageId) ? "Bỏ ghim" : "Ghim tin nhắn"}
            </div>
            <div
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleMarkMessage(contextMenu.messageId)}
            >
              <FaStar className="mr-2" />{" "}
              {markedMessages[activeChatUserId]?.includes(contextMenu.messageId) ? "Bỏ đánh dấu" : "Đánh dấu tin nhắn"}
            </div>
          </>
        )}
      </motion.div>
    )
  )}
</AnimatePresence>
      </div>
    );
  };

  export default ChatWidgetParticipants;